import type { GenericRecordData } from '@lemonade-hq/maschema-schema';
import type { UniclientSummary } from '@lemonade-hq/uniclient-chat';
import {
  Card,
  spacing,
  useDelayedValue,
  useObserveElementVisibility,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, cubicBezier, motion } from 'motion/react';
import { Price } from '../adapters/components/Price/Price';
import { WIZARD_CONTAINER_ELEMENT_ID } from '../utils/consts';
import type { WizardOnAction } from './types';
import * as styles from './WizardSummary.css';
import { WizardSummaryDrawer } from './WizardSummaryDrawer';

interface WizardSummaryProps {
  readonly answers?: GenericRecordData;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly summary?: UniclientSummary;
  readonly hidden?: boolean;
  readonly drawerExternalClassName?: string;
  readonly animationDelay?: number;
}

const OUT_OF_SCREEN_TRANSLATE_X = '100%';

export const WizardSummary: React.FC<WizardOnAction & WizardSummaryProps> = ({
  answers,
  disabled,
  loading,
  onAction,
  summary,
  hidden = false,
  drawerExternalClassName,
  animationDelay = 0,
}) => {
  const { isBelowDesktopViewport } = useViewportSize();
  const summaryEntry = useObserveElementVisibility({ id: summary?.hideWhenIdVisible });
  const wizardContainerEntry = useObserveElementVisibility({ id: WIZARD_CONTAINER_ELEMENT_ID });
  const wizardContainerVisible = wizardContainerEntry?.isIntersecting ?? true;
  const shouldShowSummary = !hidden && !(summaryEntry?.isIntersecting ?? false);
  const delayedSummary = useDelayedValue(summary != null, false, animationDelay);
  const showSummary = delayedSummary && shouldShowSummary;

  return (
    <AnimatePresence>
      {summary != null &&
        (isBelowDesktopViewport ? (
          <WizardSummaryDrawer
            answers={answers}
            disabled={disabled}
            externalClassName={drawerExternalClassName}
            hidden={!wizardContainerVisible}
            loading={loading}
            onAction={onAction}
            open={showSummary}
            summary={summary}
          />
        ) : (
          showSummary && (
            <motion.div
              animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}
              exit={{ x: OUT_OF_SCREEN_TRANSLATE_X, opacity: 0 }}
              initial={{ x: OUT_OF_SCREEN_TRANSLATE_X, opacity: 0 }}
              transition={{
                ease: cubicBezier(0.5, 0, 0.25, 1.25),
              }}
            >
              <Card
                aria-label="summary card"
                borderRadius="md"
                className={styles.card}
                gap={spacing.s16}
                px={spacing.s16}
                py={spacing.s16}
                shadow="lg"
              >
                <Price
                  {...summary}
                  answers={answers}
                  buttonSize="sm"
                  disabled={disabled}
                  loading={loading}
                  onAction={onAction}
                  variant="tiny"
                />
              </Card>
            </motion.div>
          )
        ))}
    </AnimatePresence>
  );
};
